import { useEffect, useState, useCallback } from 'react';
import type { Bot } from '@/types/bot';
import { botAPI } from '@/services/api';
import { useWebSocket } from '@/hooks/useWebSocket';

export function useLiveBots() {
  const [bots, setBots] = useState<Bot[]>([]);
  const { connected, subscribe, unsubscribe } = useWebSocket();

  // Initial load from REST API
  useEffect(() => {
    botAPI.getAll()
      .then((data) => {
        console.log('[useLiveBots] Loaded', data.length, 'bots from API');
        setBots(data);
      })
      .catch((err) => console.warn('[useLiveBots] REST fetch failed:', err.message));
  }, []);

  // Subscribe to BOT_STATUS messages via shared WebSocket
  useEffect(() => {
    const handler = (data: unknown) => {
      const botData = data as Partial<Bot> & { symbol: string };
      if (!botData.symbol) return;

      setBots((prev) => {
        const idx = prev.findIndex((b) => b.symbol === botData.symbol);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { ...updated[idx], ...botData };
          return updated;
        } else {
          return [...prev, botData as Bot];
        }
      });
    };

    subscribe('BOT_STATUS', handler);
    return () => unsubscribe('BOT_STATUS', handler);
  }, [subscribe, unsubscribe]);

  const startBot = useCallback(async (id: string, otp?: string) => {
    try {
      await botAPI.start(id, otp);
      setBots(prev => prev.map(b => b.id === id ? { ...b, status: 'active' } : b));
    } catch (err) {
      console.error('[useLiveBots] Failed to start bot:', err);
    }
  }, []);

  const stopBot = useCallback(async (id: string) => {
    try {
      await botAPI.stop(id);
      setBots(prev => prev.map(b => b.id === id ? { ...b, status: 'stopped' } : b));
    } catch (err) {
      console.error('[useLiveBots] Failed to stop bot:', err);
    }
  }, []);

  return { bots, connected, startBot, stopBot };
}
